import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { tokenStore } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const { companyName } = useBranding();
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [status, setStatus] = useState("checking"); // checking | valid | invalid
  const [errorMsg, setErrorMsg] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function validate() {
      if (!token) {
        setStatus("invalid");
        setErrorMsg("Länken saknar en giltig token.");
        return;
      }
      try {
        const { data } = await axios.get(
          `${API}/auth/reset-password/${encodeURIComponent(token)}`
        );
        if (!cancelled) {
          setEmail(data?.email || "");
          setStatus("valid");
        }
      } catch (err) {
        if (cancelled) return;
        const detail = err?.response?.data?.detail;
        setErrorMsg(
          typeof detail === "string"
            ? detail
            : "Länken är ogiltig eller har gått ut."
        );
        setStatus("invalid");
      }
    }
    validate();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Lösenordet måste vara minst 8 tecken");
      return;
    }
    if (password !== confirm) {
      toast.error("Lösenorden matchar inte");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/auth/reset-password`,
        { token, new_password: password },
        { withCredentials: true }
      );
      if (r.data?.access_token || r.data?.refresh_token) {
        tokenStore.set(r.data.access_token, r.data.refresh_token);
      }
      toast.success("Lösenordet är uppdaterat");
      if (checkAuth) await checkAuth();
      navigate("/", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Vi kunde inte uppdatera lösenordet. Försök igen."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="reset-password-page"
      className="min-h-screen flex items-center justify-center bg-cover bg-center relative"
      style={{
        backgroundImage:
          'url("https://images.unsplash.com/photo-1591638212063-c2f69701a931?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHx3b29kd29ya2luZyUyMHN0YWlycyUyMG1vZGVybiUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODAxNDQzMDh8MA&ixlib=rb-4.1.0&q=85")',
      }}
    >
      <div className="absolute inset-0 bg-black/55" />
      <div className="relative z-10 bg-surface p-10 md:p-12 rounded-2xl max-w-md w-full mx-4 border border-app">
        <div className="mb-2 text-xs tracking-[0.2em] uppercase text-text-muted">
          {companyName}
        </div>
        <h1 className="font-heading text-3xl md:text-4xl tracking-tight text-text-primary leading-[1.1]">
          Välj nytt lösenord
        </h1>

        {status === "checking" && (
          <p
            className="mt-6 text-sm text-text-muted"
            data-testid="reset-password-checking"
          >
            Kontrollerar länken…
          </p>
        )}

        {status === "invalid" && (
          <div
            data-testid="reset-password-invalid"
            className="mt-6 space-y-4"
          >
            <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-4 text-sm text-text-primary">
              {errorMsg}
            </div>
            <Link
              to="/forgot-password"
              data-testid="reset-password-request-new"
              className="tk-btn-primary w-full block text-center py-3"
            >
              Begär ny länk
            </Link>
            <Link
              to="/login"
              className="text-brand hover:underline text-sm block text-center"
              data-testid="reset-password-back-to-login"
            >
              Tillbaka till inloggning
            </Link>
          </div>
        )}

        {status === "valid" && (
          <>
            <p className="mt-4 text-text-muted text-sm">
              Konto: <strong className="text-text-primary">{email}</strong>.
              Välj ett nytt lösenord (minst 8 tecken).
            </p>
            <form
              onSubmit={handleSubmit}
              className="mt-8 space-y-4"
              data-testid="reset-password-form"
            >
              <div>
                <label
                  className="block text-xs text-text-muted mb-1.5"
                  htmlFor="password"
                >
                  Nytt lösenord
                </label>
                <input
                  id="password"
                  type="password"
                  required
                  minLength={8}
                  data-testid="reset-password-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  autoFocus
                  className="tk-input w-full"
                  placeholder="••••••••"
                />
              </div>
              <div>
                <label
                  className="block text-xs text-text-muted mb-1.5"
                  htmlFor="confirm"
                >
                  Bekräfta nytt lösenord
                </label>
                <input
                  id="confirm"
                  type="password"
                  required
                  minLength={8}
                  data-testid="reset-password-confirm-input"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  className="tk-input w-full"
                  placeholder="••••••••"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="reset-password-submit-button"
                className="tk-btn-primary w-full mt-2"
              >
                {submitting ? "Uppdaterar…" : "Uppdatera lösenord"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
